import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { useAllBasisTypesQuery, CreateBasisTypePriceMutationVariables, useCreateBasisTypePriceMutation, SortEnumType } from '../../../generated/graphql'
import Modal from '../../Modal/Modal'
import Button, { ButtonType } from '../../General/Button/Button'
import styles from '../BulkUpdateSupplierPostingsModal/BulkUpdateSupplierPostingsModal.module.scss'
import inputStyles from '../../General/Inputs/Input.module.scss'
import DatePicker from 'react-datepicker'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/client'

// Add the bulk update mutation
export const BulkUpdateBasisTypePricesMutation = gql`
   mutation BulkUpdateBasisTypePrices($prices: [BasisTypePriceInput]!) {
      basisTypePriceBulkUpdate(prices: $prices)
   }
`

interface Props {
    closeModal(): void
    callback?(): void
}

interface BasisTypeUpdate {
    key: number
    price: string
    date: Date
    error?: string // Add error field for individual basis type errors
}

const BasisTypeUpdatePricesModal = (props: Props) => {

    const { data, loading, error } = useAllBasisTypesQuery({ variables: { sortObject: { name: SortEnumType.Asc } } })
    const [date, setDate] = useState<Date>(new Date(moment().format('LL')))
    const [basisTypes, setBasisTypes] = useState<BasisTypeUpdate[]>([])
    const [submitError, setSubmitError] = useState<string>('') // Add general error state
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false) // Add loading state

    const [createBasisTypePrice] = useCreateBasisTypePriceMutation()
    const [bulkUpdateBasisTypePrices] = useMutation(BulkUpdateBasisTypePricesMutation)

    useEffect(() => {
        if (data) {
            const arr: BasisTypeUpdate[] = data.basisTypeGetAll.map(p => {
                const transformedVal: BasisTypeUpdate = { key: p.basisTypeKey, price: p.currentPrice.price.toFixed(4), date: p.currentPrice.effectiveDate }
                return transformedVal
            })
            setBasisTypes(arr)
        }
    }, [setBasisTypes, data])


    if (loading) return <Modal></Modal>
    if (error) return <Modal><h2>Error</h2></Modal>

    function handleChange(i, event) {
        let newPrices: BasisTypeUpdate[] = Object.assign(basisTypes)
        newPrices[i].price = event.target.value
        setBasisTypes(newPrices)
    }

    const submit = async () => {
        setIsSubmitting(true)
        setSubmitError('') // Clear previous errors

        // Clear individual basis type errors
        setBasisTypes(prevTypes => prevTypes.map(basisType => ({ ...basisType, error: undefined })))

        let pricesToUpdate = []

        basisTypes.forEach(c => {
            let btInt = Number(c.price)
            if (!isNaN(btInt)) {
                const bt = data.basisTypeGetAll.find(bt => bt.basisTypeKey === c.key)
                if (bt) {
                    if (bt.currentPrice.price === btInt) {
                        return
                    }

                    let basisTypePrice = {
                        price: btInt,
                        basisTypeKey: c.key,
                        effectiveDate: date
                    }

                    pricesToUpdate.push(basisTypePrice)
                }
            }
        })

        try {
            const result = await bulkUpdateBasisTypePrices({ variables: { prices: pricesToUpdate } })

            if (result.data?.basisTypePriceBulkUpdate) {
                // Success - execute callback and close modal
                if (props.callback) {
                    await props.callback()
                }
                props.closeModal()
            }
        } catch (error) {
            setIsSubmitting(false)

            console.error('Bulk update error:', error) // Debug logging

            // Extract detailed error information from GraphQL error structure
            let errorMessage = 'An unexpected error occurred'

            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                const graphQLError = error.graphQLErrors[0]

                // Check if the detailed message is in extensions (which is where our validation errors are)
                if (graphQLError.extensions && graphQLError.extensions.message) {
                    errorMessage = graphQLError.extensions.message
                } else {
                    // Fallback to the main message
                    errorMessage = graphQLError.message
                }
            } else if (error.networkError) {
                // Network errors
                errorMessage = `Network error: ${error.networkError.message}`
            } else if (error.message) {
                // General error message
                errorMessage = error.message
            }

            console.log('Parsed error message:', errorMessage) // Debug logging

            // Check if it's a validation error with specific basis type details
            if (errorMessage.includes('Basis Type Price ') && (errorMessage.includes('overlap') || errorMessage.includes('validation') || errorMessage.includes('existing'))) {
                // Extract basis type-specific errors and map them to individual basis types
                const lines = errorMessage.split('\n')
                const validationErrors = lines.filter(line => line.includes('Basis Type Price '))

                console.log('Validation errors found:', validationErrors) // Debug logging

                // Update basis types with individual errors
                setBasisTypes(prevTypes => {
                    return prevTypes.map(basisType => {
                        const basisTypeError = validationErrors.find(error => error.includes(`Basis Type: ${basisType.key}`))
                        if (basisTypeError) {
                            // Extract just the error part after the basis type info
                            const errorPart = basisTypeError.split('): ')[1] || basisTypeError
                            return { ...basisType, error: errorPart }
                        }
                        return basisType
                    })
                })

                setSubmitError('Some basis type prices have validation errors. Please review the highlighted items below.')
            } else {
                // General error - show the full detailed message
                setSubmitError(errorMessage)
            }
        }
    }

    const footerLeftContent = (<Button clickAction={props.closeModal} buttonType={ButtonType.Secondary}>Cancel</Button>)
    const footerRightContent = (
        <Button
            clickAction={submit}
            buttonType={ButtonType.Success}
            isDisabled={isSubmitting}
        >
            {isSubmitting ? 'Updating...' : 'Update Basis Types'}
        </Button>
    )

    const onDateChange = (date: Date, e) => {
        setDate(date)
    }

    return (
        <Modal headerText="Update All Basis Types" footerLeftContent={footerLeftContent} footerRightContent={footerRightContent}>

            {/* Display general error message */}
            {submitError && (
                <div style={{
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    border: '1px solid #f5c6cb'
                }}>
                    <strong>Error:</strong> {submitError}
                    {/* Show additional debug info in development */}
                    {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                        <details style={{ marginTop: '8px' }}>
                            <summary style={{ cursor: 'pointer', color: '#495057' }}>Debug Details (Development Only)</summary>
                            <pre style={{
                                fontSize: '11px',
                                backgroundColor: '#f8f9fa',
                                padding: '8px',
                                marginTop: '4px',
                                color: '#495057',
                                overflow: 'auto',
                                maxHeight: '200px'
                            }}>
                                Check browser console for full error details
                            </pre>
                        </details>
                    )}
                </div>
            )}

            <div className={`row ${styles.tableHeading}`}>
                <div className="col-md-3">Basis Type</div>
                <div className="col-md-3">Date</div>
                <div className="col-md-3">Price</div>
                <div className="col-md-3">New Price</div>
            </div>

            <div className={styles.tableContainer}>
                {data.basisTypeGetAll.map((bt, index) => {
                    const basisTypeUpdate = basisTypes.find(b => b.key === bt.basisTypeKey)
                    const hasError = basisTypeUpdate?.error

                    return (<div className={`row ${styles.tableRow}`} key={index} style={{
                        backgroundColor: hasError ? '#f8d7da' : undefined,
                        border: hasError ? '1px solid #f5c6cb' : undefined
                    }}>
                        <div className={`col-md-3 ${styles.tableRowColumn}`}>{bt.name}</div>
                        <div className={`col-md-3 ${styles.tableRowColumn}`}>{moment.utc(bt.currentPrice.effectiveDate).format('LL')}</div>
                        <div className={`col-md-3 ${styles.tableRowColumn}`}>{bt.currentPrice.price.toFixed(4)}</div>
                        <div className="col-md-3">
                            <input
                                type="text"
                                placeholder="New Price"
                                onChange={e => handleChange(index, e)}
                                className={inputStyles.input}
                                defaultValue={bt.currentPrice.price.toFixed(4)}
                                style={{
                                    borderColor: hasError ? '#dc3545' : undefined,
                                    backgroundColor: hasError ? '#fff5f5' : undefined
                                }}
                            />
                            {/* Display individual basis type error */}
                            {hasError && (
                                <div style={{
                                    color: '#dc3545',
                                    fontSize: '12px',
                                    marginTop: '4px',
                                    fontWeight: 'bold'
                                }}>
                                    {basisTypeUpdate.error}
                                </div>
                            )}
                        </div>
                    </div>)
                })}
            </div>
            <div className="row">
                <div className="col-md-4">
                    <label className={inputStyles.input__wrapper}>Date</label>
                    <DatePicker
                        className={inputStyles.input}
                        selected={date}
                        onChange={onDateChange}
                        showTimeSelect={false}
                        dateFormat="MMMM d, yyyy"
                        maxDate={new Date()}
                    />
                </div>
            </div>
        </Modal>
    )
}
export default BasisTypeUpdatePricesModal